import { useMemo, useCallback, memo } from 'react'
import { UseFieldConfig } from 'react-final-form'
import { useStores } from 'stores/utils/hooks/useStores'
import { observer } from 'mobx-react'
import { Debtor } from 'interfaces/ApolloResponse.interface'
import { RdiCost, RdiCosts } from 'containers/Rdi/rdiConstIndication'
import WithIdleFieldChange from 'components/WithIdleFieldChange'
import FieldDataCollector from '../FieldDataCollector'
import { PathFormFieldProps } from '../../interfaces/PathFormComponents.interface'
import WithFieldInteraction from '../../utils/tracking/amplitude/WithFieldInteraction'
import { useMeta } from '../../stores/utils/hooks/useMeta'
import { usePatchedField } from '../../stores/utils/hooks/usePatchedField'
import ChoiceGroup, { Choice } from '../Input/ChoiceGroup'

type Props<T> = Pick<
  PathFormFieldProps<T>,
  Exclude<keyof PathFormFieldProps, 'onChange'>
> & {
  debtor?: Debtor
  defaultValue?: T
  onChange?: (value: T) => void
  clue?: string
  choices?: Choice<T>[]
  label?: string
  forceFullWidth?: boolean
  rdiCosts?: RdiCosts // TODO Remove as soon as Rdi ChoiceGroup is an independent Component
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fieldConfig?: UseFieldConfig<any>
}

// TODO Remove as soon as Rdi ChoiceGroup is an independent Component
const addCostIndicationToOptionLabel = (
  label: string,
  rdiCost: RdiCost
): string =>
  rdiCost
    ? `${label.split(' ')[0]} (ab mtl. ${rdiCost
        .toFixed(2)
        .replace('.', ',')}€*)`
    : label

const ChoiceFieldGroup = observer(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  <T extends any = string>({
    path,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelPath,
    clue,
    rdiCosts, // TODO Remove as soon as Rdi ChoiceGroup is an independent Component
    defaultValue,
    onChange,
    fieldConfig,
    forceFullWidth = false,
    label: propLabel,
    choices: propChoices,
    ...restProps
  }: Props<T>) => {
    const { input, error } = usePatchedField(path, {
      initialValue: defaultValue,
      ...fieldConfig
    })

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { label, options = [], tooltip, ...restMeta } = useMeta<T>(path)

    // TODO Remove as soon as Rdi ChoiceGroup is an independent Component
    const modifiedChoices = useMemo(() => {
      const choices = propChoices || options

      if (!rdiCosts) {
        return choices
      }

      const costLabels = [
        rdiCosts.UNEMPLOYMENT_DEATH_INABILITY,
        rdiCosts.DEATH_INABILITY,
        rdiCosts.DEATH
      ]

      return choices.map((choice, index) => ({
        ...choice,
        label: addCostIndicationToOptionLabel(choice.label, costLabels[index])
      }))
    }, [propChoices, options, rdiCosts])

    const {
      traversal: { removeErrorsForPath }
    } = useStores()

    const changeHandler = useCallback(
      (value: T) => {
        input.onChange(value)
        input.onBlur()

        if (onChange) {
          onChange(value)
        }

        removeErrorsForPath(path)
      },
      [input, onChange, removeErrorsForPath, path]
    )

    return (
      <WithFieldInteraction trackOnChange path={path}>
        <WithIdleFieldChange delayInMs={300}>
          <ChoiceGroup<T>
            name={input.name}
            value={input.value}
            showTooltip={restProps.showTooltip}
            label={propLabel || label}
            choices={modifiedChoices}
            clue={clue}
            tooltip={tooltip}
            error={error}
            forceFullWidth={forceFullWidth}
            onChange={changeHandler}
          />
        </WithIdleFieldChange>
      </WithFieldInteraction>
    )
  }
)

function PathChoiceFieldGroup<T = string>(props: Props<T>) {
  const { path, labelPath } = props

  return (
    <FieldDataCollector path={path} labelPath={labelPath}>
      <ChoiceFieldGroup<T> {...props} />
    </FieldDataCollector>
  )
}

export default memo(PathChoiceFieldGroup) as typeof PathChoiceFieldGroup
